import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./TemperatureSensor.scss";
import viewicon from "../../assets/images/view-icon.svg";
import threedotvertical from "../../assets/images/three-dots-vertical.svg";
import play from "../../assets/images/play.svg";
import stop from "../../assets/images/stop-circle.svg";
import areaChartIcon from "../../assets/images/area_chart_icon.svg";
import thermometerIcon from "../../assets/images/thermometer-icon.svg";
import { ReactSVG } from "react-svg";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import useSensors from "../../Hooks/useSensor";
import { toggleClass, truncateText } from "../../utils/commonUtils";
import Loading from "../../components/LoadingPage/Loading";
import { toast } from "react-toastify";
import GraphModel from "../../components/graphModel/graphModel";
import { setCurrentSensor } from "../../redux/sensor/sensorSlice";
import BatteryDisplay from "../../components/BatteryDisplay/BatteryDisplay";
import useRefrigerators from "../../Hooks/useRefrigerators";
import TemperatureDisplayDialog from "../../components/temperatureDisplay/TemperatureDisplayDialog";

const TemperatureSensor = () => {
  const [sensorsList, setSensorsList] = useState<any[]>([]);
  const [refrigeratorList, setRefrigeratorList] = useState<any[]>([]);
  const [showGraphModel, setShowGraphModel] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<any>();
  const [isTempDisplayOpen, setIsTempDisplayOpen] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState("ClientCode");
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUserId = localStorage.getItem("profileClientId")?.toUpperCase() ?? "";
  const { getRefrigeratorsAPI, getRefrigeratorsLoading } = useRefrigerators();
  const {
    getSensorListAPI,
    getSensorLoading,
    startSensorAPI,
    startSensorLoading,
    stopSensorAPI,
    stopSensorLoading,
  } = useSensors();

  const handleSort = useCallback((column: string) => {
    setSortColumn(prev => {
      const direction = prev === column ? (sortDirection === "asc" ? "desc" : "asc") : "asc";
      setSortDirection(direction);
      return column;
    });
  }, [sortDirection]);

  const handleOpenGraphModal = useCallback((sensor: any) => {
    toggleClass();
    if (sensor) {
      setSelectedSensor(sensor);
      setShowGraphModel(true);
      return;
    }
    toast.error("No temperature logs found for this refrigerator");
  }, [toggleClass]);

  const handleCloseGraphModal = useCallback(() => {
    toggleClass();
    setSelectedSensor(null);
    setShowGraphModel(false);
  }, [toggleClass]);

  const handleOpenTempDisplayModal = useCallback((sensor: any) => {
    setSelectedSensor(sensor);
    setIsTempDisplayOpen(true);
  }, []);

  const handleCloseTempDisplayModal = useCallback(() => {
    setSelectedSensor(null);
    setIsTempDisplayOpen(false);
  }, []);

  const fetchSensorList = useCallback(async () => {
    try {
      const res = await getSensorListAPI({ tenantId: currentUserId, sortColumn, sortDirection });
      setSensorsList(res || []);
    } catch (error) {
      console.error("Failed to get sensor list", error);
      setSensorsList([]);
    }
  }, [currentUserId, sortColumn, sortDirection, getSensorListAPI]);

  const fetchRefrigeratorList = useCallback(async () => {
    try {
      const res = await getRefrigeratorsAPI({ clientCode: currentUserId });
      setRefrigeratorList(res || []);
    } catch (error) {
      console.error("Failed to get refrigerator list", error);
      setRefrigeratorList([]);
    }
  }, [currentUserId, getRefrigeratorsAPI]);

  // Re-fetch sensors when sort changes
  useEffect(() => {
    fetchSensorList();
    fetchRefrigeratorList();
  }, []);

  const handleStartSensor = useCallback(async (sensorId: string) => {
    try {
      const res = await startSensorAPI({ id: sensorId });
      if (res) {
        toast.success(res?.message);
        fetchSensorList();
      }
    } catch (err) {
      console.error("Failed to start sensor", err);
    }
  }, [startSensorAPI, fetchSensorList]);

  const handleStopSensor = useCallback(async (sensorId: string) => {
    try {
      const res = await stopSensorAPI({ id: sensorId });
      if (res) {
        toast.success(res?.message);
        fetchSensorList();
      }
    } catch (err) {
      console.error("Failed to stop sensor", err);
    }
  }, [stopSensorAPI, fetchSensorList]);

  const navigateToView = useCallback((data: any) => {
    dispatch(setCurrentSensor(data));
    navigate("/manage-sensor");
  }, [dispatch, navigate]);

  const renderToolTip = useCallback((data: any) => {
    if (!data) return <span />;

    const emails = data?.email?.split(",").map((e: string) => e.trim()).filter(Boolean) ?? [];
    const contacts = data?.contactNo?.split(",").map((c: string) => c.trim()).filter(Boolean) ?? [];
    const hasData = emails.length > 0 || contacts.length > 0;

    return (
      <Popover id={`alarm-details-${data.id}`} className="ref-alarm-popover">
        <Popover.Header>Alarm Contacts</Popover.Header>
        <Popover.Body>
          {!hasData && <p className="no-contact-data">No contacts configured</p>}
          {emails.length > 0 && (
            <ul className="contact-section">
              <li className="contact-section-label">Email</li>
              {emails.map((e: string) => (
                <li key={e} className="contact-item">{e}</li>
              ))}
            </ul>
          )}
          {contacts.length > 0 && (
            <ul className="contact-section">
              <li className="contact-section-label">Phone</li>
              {contacts.map((c: string) => (
                <li key={c} className="contact-item">{c}</li>
              ))}
            </ul>
          )}
        </Popover.Body>
      </Popover>
    );
  }, []);

  const mergedRefrigeratorList = useMemo(() => {
    if (!refrigeratorList?.length) return [];

    const sensorMap = new Map(sensorsList.map(sensor => [sensor.id, sensor]));

    return refrigeratorList.map(item => ({
      ...item,
      sensor: item.sensorRefrigerator?.sensorId ? sensorMap.get(item.sensorRefrigerator.sensorId) || null : null,
    }));
  }, [refrigeratorList, sensorsList]);

  const isLoading = useMemo(() => {
    return getSensorLoading || getRefrigeratorsLoading;
  }, [getSensorLoading, getRefrigeratorsLoading]);

  return (
    <div className="sensor-listing-wrapper">
      {isLoading ? (<Loading />) : null}
      <div className="sensor-header">
        <h2>Refrigerators</h2>
      </div>
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th>Refrigerator Name</th>
              <th className="cursor-pointer" onClick={() => handleSort("SerialNumber")}>
                Sensor Id {sortColumn === "SerialNumber" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th>Refrigerator Type</th>
              <th>Refrigerator Location</th>
              <th className="text-center">Low/High Temp.</th>
              <th className="text-center">Interval Duration</th>
              <th className="text-center">Alarm Status</th>
              <th className="text-center">Status</th>
              <th className="text-center">Battery Level</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {mergedRefrigeratorList?.map((item: any) => (
              <tr key={item.id} onDoubleClick={() => navigateToView(item)}>
                <td title={item.refrigeratorName}>{item.refrigeratorName}</td>
                <td title={item.sensor?.serialNumber}>{item.sensor?.serialNumber}</td>
                <td title={item?.type}>{item?.type}</td>
                <td title={item?.placement}>{truncateText(item?.placement, 20)}</td>
                <td title={item.sensor?.temperatureDisplay} className="text-center">{item.sensor?.temperatureDisplay}</td>
                <td title={item.sensor?.intervalDisplay} className="text-center">{item.sensor?.intervalDisplay}</td>
                <td className="text-center">
                  <OverlayTrigger overlay={renderToolTip(item)}>
                    <span className={`tbl-badge ${item.sensor?.alarmActive ? 'active' : ''}`}>
                      {item.sensor ? (item.sensor?.alarmActive ? "On" : "Off") : ""}
                    </span>
                  </OverlayTrigger>
                </td>
                <td title={item.sensor?.sensorTaskStatus == null ? "No" : "Yes"} className="text-center">
                  <span className={`tbl-badge ${item.sensor?.sensorTaskStatusColor !== null ? `active text-${item.sensor?.sensorTaskStatusColor?.toLowerCase()}` : ''}`}>
                    {item.sensor?.sensorTaskStatus !== null ? item.sensor?.sensorTaskStatus : ''}
                  </span>
                </td>
                <td title={item.sensor?.batteryLevel} className="text-center">{item.sensor?.batteryLevel ? <BatteryDisplay level={item.sensor?.batteryLevel ?? 0} /> : ''}</td>
                <td>
                  <Dropdown>
                    <Dropdown.Toggle variant="link" id="dropdown-basic">
                      <ReactSVG className="svg-box" wrapper="span" src={threedotvertical} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => navigateToView(item)}>
                        <ReactSVG className="svg-box" wrapper="span" src={viewicon} /> View
                      </Dropdown.Item>
                      {item.sensor && (
                        <>
                          {item.taskId == null ? (
                            <Dropdown.Item onClick={() => handleStartSensor(item.sensor?.id)}>
                              <ReactSVG className="svg-box" wrapper="span" src={play} /> Start Sensor
                            </Dropdown.Item>
                          ) : (
                            <Dropdown.Item onClick={() => handleStopSensor(item.sensor?.id)}>
                              <ReactSVG className="svg-box" wrapper="span" src={stop} /> Stop Sensor
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item onClick={() => handleOpenTempDisplayModal(item.sensor)}>
                            <ReactSVG className="svg-box" wrapper="span" src={thermometerIcon} /> Temp Display
                          </Dropdown.Item>
                        </>
                      )}
                      <Dropdown.Item onClick={() => handleOpenGraphModal(item)}>
                        <ReactSVG className="svg-box" wrapper="span" src={areaChartIcon} /> Temp Log
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {
        showGraphModel && (
          <GraphModel
            showModal={showGraphModel}
            handleClose={handleCloseGraphModal}
            modelName={`Temperature Log`}
            sensorData={{
              id: selectedSensor?.sensor?.id || "",
              inCentigrade: Boolean(selectedSensor?.sensor?.inCentigrade),
              refId: selectedSensor?.id || "",
            }}
          />
        )
      }

      {
        isTempDisplayOpen && (
          <TemperatureDisplayDialog
            sensorId={selectedSensor?.id}
            isOpen={isTempDisplayOpen}
            handleClose={handleCloseTempDisplayModal}
          />
        )
      }
    </div >
  );
};
export default TemperatureSensor;
